/* eslint-disable max-statements */

import { Box, Button, Checkbox, Chip, Divider, Grid, Group, Stack, Text, Title } from '@mantine/core';
import _ from 'lodash';
import { useEffect, useState } from 'react';

import { Declaration, DeclarationGoods, IRiskAnalysisDescriptionNewValues } from '@e-origin/shared';

import { rightArrowIcon, translationIcon, viewImageIcon } from '@assets';
import { TextInput } from '@components';
import { RISK_INITIAL_WEB_DESC_LIMIT } from '@constants';
import { useDownloadScreenshot } from '@hooks/declaration';
import { riskAnalysisService } from '../../../services';
import { GoodsItemUploadScreenshot } from '../../DeclarationView';
import { ValidateAllStateEnum } from '../RiskAnalysis';
import classes from './RiskAnalysisDescription.module.css';

type Chips = Record<
  string,
  {
    value: boolean;
    customColor?: string;
  }
>;

interface RiskAnalysisDescriptionProps {
  declaration: Declaration;
  goodDetailsOriginal: DeclarationGoods;
  goodDetails: DeclarationGoods;
  update: (obj: IRiskAnalysisDescriptionNewValues) => void;
  runAiModule: () => void;
  validate: ValidateAllStateEnum;
}

export const RiskAnalysisDescription: React.FC<RiskAnalysisDescriptionProps> = ({
  declaration,
  goodDetails,
  goodDetailsOriginal,
  update,
  runAiModule,
  validate,
}) => {
  const downloadScreenshotFromLinkMutation = useDownloadScreenshot();
  const initialWebDescription: string =
    (goodDetails.webScraping?.webDescriptionOfGoods || '')
      .split(' ')
      .filter((word: string) => word.trim().length)
      .slice(0, RISK_INITIAL_WEB_DESC_LIMIT)
      .join(' ') || '';

  const suggestedDescriptionOfGoods = goodDetails?.evalMethodDescription?.suggestedDescriptionOfGoods || '';
  const initialSuggestedDescriptionOfGoods = suggestedDescriptionOfGoods.length === 0 ? [] : suggestedDescriptionOfGoods.split(' ');
  const chipsValues = initialWebDescription.split(' ').reduce<Chips>(
    (acc: Chips, curr: string) => {
      if (curr) {
        acc[curr] = { value: initialSuggestedDescriptionOfGoods.includes(curr) };
      }
      return acc;
    },
    goodDetails.commodity?.material
      ? ({
          [goodDetails.commodity?.material]: {
            value: initialSuggestedDescriptionOfGoods.includes(goodDetails.commodity?.material),
            customColor: '#1770A3',
          },
        } as Chips)
      : {},
  );

  const [validatedDescriptionCheck, setValidatedDescriptionCheck] = useState(!!goodDetails.evalMethodDescription?.validatedDescriptionCheck);
  const validatedDescriptionCheckDisabled = riskAnalysisService.evalMethodDescriptionCheckDisabled(goodDetailsOriginal);

  const [chips, setChips] = useState(chipsValues);
  const [newDescription, setNewDescription] = useState<string>(suggestedDescriptionOfGoods);
  const [showUploadScreenshotModal, setShowUploadScreenshotModal] = useState(false);

  const emptyDescription = (value: string) => {
    if (!value) {
      setChips(
        _.mapValues(chips, (chip) => ({
          ...chip,
          value: false,
        })),
      );
    }
    setNewDescription(value || '');
  };

  useEffect(() => {
    setNewDescription(suggestedDescriptionOfGoods);
    setValidatedDescriptionCheck(!!goodDetails.evalMethodDescription?.validatedDescriptionCheck);
    setChips(chipsValues);
  }, [goodDetails]);

  useEffect(() => {
    if (validate === null || validate === ValidateAllStateEnum.SOME_SELECTED) {
      return;
    }
    setValidatedDescriptionCheck(validate === ValidateAllStateEnum.ALL_SELECTED && !validatedDescriptionCheckDisabled);
  }, [validate]);

  useEffect(() => {
    const newDescriptionWords = newDescription.replace(/[^a-zA-Z0-9 ]+/g, '').split(' ');
    Object.keys(chipsValues).forEach((chip) => {
      chipsValues[chip].value = newDescriptionWords.includes(chip);
    });
    setChips(chipsValues);
  }, [newDescription]);

  useEffect(() => {
    update({
      newDescription,
      validatedDescriptionCheck,
      validatedDescriptionCheckDisabled,
    });
  }, [newDescription, validatedDescriptionCheck]);

  const webScrapingDone = !!declaration?.generalInfo?.webScrapingDone;

  const toggleChip = (chipName: string) => {
    const newChips = {
      ...chips,
      [chipName]: {
        ...chips[chipName],
        value: !chips[chipName],
      },
    };
    setChips(newChips);
    if (newDescription.split(' ').includes(chipName)) {
      const newDescriptionWords = newDescription.split(' ');
      setNewDescription(newDescriptionWords.filter((item: any) => item !== chipName).join(' '));
    } else {
      setNewDescription(`${newDescription} ${chipName}`);
    }
  };

  const toggleShowUploadScreenshotModal = () => {
    setShowUploadScreenshotModal(!showUploadScreenshotModal);
  };

  const screenshotLink =
    goodDetails.webScraping.webAdditionalFiles.find((webAdditionalFile: any) => {
      return webAdditionalFile.type === 'screenshot';
    })?.url || '';

  const downloadScreenshot = () => {
    if (screenshotLink) {
      downloadScreenshotFromLinkMutation.mutate({ screenshotLink });
    }

    if (!screenshotLink && webScrapingDone) {
      toggleShowUploadScreenshotModal();
    }
  };

  const computeWebMatchingDescriptionStatus = () => {
    if (!goodDetails.evalMethodDescription?.webMatchingDescription) {
      return {
        badgeColor: 'red',
        badgeMessage: 'Match not ok',
      };
    }

    if (goodDetails.evalMethodDescription?.suggestedDescriptionOfGoods) {
      return {
        badgeColor: 'green',
        badgeMessage: 'Suggested',
      };
    }

    return {
      badgeColor: 'green',
      badgeMessage: 'Match ok',
    };
  };

  const RenderChips = () => {
    return (
      <Box className={classes.chipContainer}>
        {Object.keys(chips).map((chipName, index) => {
          const isActive = chips[chipName].value;
          const customColor = chips[chipName].customColor;

          return (
            <Chip
              key={chipName}
              checked={isActive}
              onClick={() => toggleChip(chipName)}
              radius="xl"
              classNames={{
                root: classes.chipRoot,
                iconWrapper: classes.chipIconWrapper,
                label: classes.chipLabel,
              }}
              styles={{
                root: {
                  border: `1px solid ${customColor ? customColor : isActive ? '#29AA47' : '#555454'}`,
                  backgroundColor: isActive ? customColor || '#29AA47' : 'transparent',
                },
                label: {
                  color: isActive ? '#FFFFFF' : customColor || '#555454',
                  backgroundColor: isActive ? customColor || '#29AA47' : 'transparent',
                },
              }}
            >
              <span data-pw={`chip-btn-${index}`}>{chipName}</span>
            </Chip>
          );
        })}
      </Box>
    );
  };

  return (
    <>
      <GoodsItemUploadScreenshot
        opened={showUploadScreenshotModal}
        declaration={declaration}
        onHide={toggleShowUploadScreenshotModal}
        url={goodDetails?.webScraping?.webUrl || ''}
        goodDetailsId={goodDetails?._id || ''}
        afterCreate={() => void 0}
      />

      <>
        <Group justify="space-between" align="center" px="md">
          <Title order={6} fw={600}>
            Description
          </Title>
          <Checkbox
            checked={validatedDescriptionCheck}
            disabled={validatedDescriptionCheckDisabled}
            onChange={(event) => {
              setValidatedDescriptionCheck(event.currentTarget.checked);
              emptyDescription('');
            }}
            label="Validate the description"
          />
        </Group>

        <Divider mt="sm" mb="lg" />

        <Box px="lg">
          <Grid align="center" mb="xl">
            <Grid.Col span={5.75}>
              <TextInput
                data-pw="invoice-description-input"
                size="lg"
                name="invoiceDescription"
                label="Invoice description"
                value={goodDetails.descriptionOfGoods || ''}
                disabled={true}
                {...computeWebMatchingDescriptionStatus()}
              />
            </Grid.Col>
            <Grid.Col span={0.5}>
              <img src={rightArrowIcon} />
            </Grid.Col>
            <Grid.Col span={5.75}>
              <TextInput
                size="lg"
                label="Web description"
                value={initialWebDescription}
                rightSection={
                  <Button variant="transparent" onClick={downloadScreenshot} p="0">
                    <img src={viewImageIcon} />
                  </Button>
                }
                disabled
              />
            </Grid.Col>
          </Grid>

          <Group mb="md">
            {goodDetails.commodity?.descriptionOfGoodsOther && (
              <Box className={classes.translationContainer}>
                <a href={`https://www.google.com/search?tbm=isch&q=${goodDetails.commodity?.descriptionOfGoodsOther}`} target="_blank">
                  {goodDetails.commodity?.descriptionOfGoodsOther}
                </a>
                <img src={translationIcon} />
                {goodDetails.commodity?.descriptionOfGoodsOtherTranslated}
              </Box>
            )}
          </Group>

          {!validatedDescriptionCheck && (
            <>
              <Box className={`${classes.newDescription} ${!goodDetails?.webScraping?.webUrl || !initialWebDescription ? classes.noPaddingBorder : ''}`}>
                {goodDetails?.webScraping?.webUrl && (initialWebDescription || goodDetails.commodity?.material) && (
                  <Box className={`${classes.formRow} ${classes.newDescriptionContainer}`}>
                    <Text fz={14} fw={600} h={25}>
                      Select words below to set the new description:
                    </Text>
                    <RenderChips />
                  </Box>
                )}
                <Stack>
                  <TextInput
                    data-pw="suggested-invoice-description-input"
                    name="newInvoiceDescription"
                    label="Suggested invoice description"
                    value={newDescription}
                    onChange={(event) => setNewDescription(event.target.value)}
                  />
                </Stack>
              </Box>

              <Box className={`${classes.formRow} ${classes.recheckButtonContainer}`}>
                <Button data-pw="recheck-hscode-btn" type="button" className={classes.recheckButton} onClick={() => runAiModule()} variant="filled" size="lg">
                  Recheck HS Code
                </Button>
              </Box>
            </>
          )}
        </Box>
      </>
    </>
  );
};
